'use client';
import React, { createContext, useContext, useState, useCallback } from 'react';

export type ViewAsPortal = 'teacher' | 'family';

export interface ViewAsProfile {
  portal: ViewAsPortal;
  profileId: string;      // teacher.id for teacher portal, student_contacts.id for family portal
  profileName: string;    // display name
  profileEmail?: string;  // for family portal — the contact email used to scope data
  teacherId?: string;     // for teacher portal — the teachers.id
}

interface ViewAsContextValue {
  viewAsProfile: ViewAsProfile | null;
  setViewAsProfile: (profile: ViewAsProfile | null) => void;
  exitViewAs: () => void;
  isViewingAs: boolean;
}

const ViewAsContext = createContext<ViewAsContextValue>({
  viewAsProfile: null,
  setViewAsProfile: () => {},
  exitViewAs: () => {},
  isViewingAs: false,
});

export const useViewAs = () => useContext(ViewAsContext);

export function ViewAsProvider({ children }: { children: React.ReactNode }) {
  const [viewAsProfile, setViewAsProfileState] = useState<ViewAsProfile | null>(null);

  const setViewAsProfile = useCallback((profile: ViewAsProfile | null) => {
    setViewAsProfileState(profile);
  }, []);

  const exitViewAs = useCallback(() => {
    setViewAsProfileState(null);
  }, []);

  return (
    <ViewAsContext.Provider
      value={{
        viewAsProfile,
        setViewAsProfile,
        exitViewAs,
        isViewingAs: viewAsProfile !== null,
      }}
    >
      {children}
    </ViewAsContext.Provider>
  );
}
